import {
    ReactFlow,
    addEdge,
    ConnectionLineType,
    Panel,
    useNodesState,
    useEdgesState,
    type Connection,
    type Node,
    type Edge,
} from '@xyflow/react'
import { Radio, Spin } from 'antd'
import { observer } from 'mobx-react-lite'
import { useCallback, useState, useEffect } from 'react'

import { userManager } from '@/Core/Managers'
import { resourceClient } from '@/Core/Services/ResourceClientService'
import { useCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'

import { DepartmentResource as resource } from '../../Resources/DepartmentResource'

import CustomNode from './CustomNode'
import { layoutElements } from './layout-elements'
import { buildFlatTree } from './transformResToTree'

import type {
    CrudContextLike,
    DepartmentFlatNode,
    DepartmentFlatTree,
    DepartmentItem,
} from '../../Contracts/DepartmentTree.types'
import '@xyflow/react/dist/style.css'
import './xy-theme.css'

export const getItemById = (
    id: string | number,
    items: DepartmentFlatTree
): DepartmentFlatNode | Record<string, never> => {
    const foundItem = Object.values(items).find((item) => item.id === id)
    return foundItem || {}
}

const nodeTypes = {
    custom: CustomNode,
}

const EntitreeFlexTreeObserver = observer(({ crudContext }: { crudContext: CrudContextLike }) => {
    const [data, setData] = useState<DepartmentFlatTree | null>(null)
    const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
    const currentUserId = userManager.user.id

    const loadData = async () => {
        const res = await resourceClient.getCollection<DepartmentItem>(resource)

        if (res && Array.isArray(res)) {
            const flatTree = buildFlatTree(res)

            setData(flatTree)
        }
    }

    useEffect(() => {
        loadData()
        if (typeof crudContext.flexTreeLoad !== 'function') {
            crudContext.flexTreeLoad = () => {
                loadData()
            }
        }
    }, [crudContext.flexTreeLoad])

    useEffect(() => {
        if (data) {
            const dataArray = Object.values(data)
            const rootItem = dataArray.find((item) => item.code === 'root')
            const rootId = rootItem ? rootItem.id : '1'

            const { nodes: newNodes, edges: newEdges } = layoutElements(data, rootId, 'TB')

            const nodesWithMethods = newNodes.map((node) => ({
                ...node,
                data: {
                    ...node.data,
                    crudContext,

                    currentUserId,
                },
            }))

            setNodes(nodesWithMethods)
            setEdges(newEdges)
        }
    }, [data, setNodes, setEdges, crudContext, currentUserId])

    const onConnect = useCallback(
        (params: Connection) =>
            setEdges((eds) =>
                addEdge(
                    {
                        ...params,
                        type: ConnectionLineType.SmoothStep,
                        animated: false,
                    },
                    eds
                )
            ),
        [setEdges]
    )

    const onLayout = useCallback(
        (direction: 'TB' | 'LR') => {
            if (!data) return
            const dataArray = Object.values(data)
            const rootItem = dataArray.find((item) => item.code === 'root')
            const rootId = rootItem ? rootItem.id : '1'

            const { nodes: newNodes, edges: newEdges } = layoutElements(data, rootId, direction)

            const nodesWithMethods = newNodes.map((node) => ({
                ...node,
                data: {
                    ...node.data,
                    crudContext,
                    currentUserId,
                },
            }))

            setNodes(nodesWithMethods)
            setEdges(newEdges)
        },
        [data, setNodes, setEdges, crudContext, currentUserId]
    )

    if (!data) {
        return (
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Spin size="large" />
            </div>
        )
    }

    return (
        <>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                connectionLineType={ConnectionLineType.SmoothStep}
                nodesDraggable={false}
                nodesConnectable={false}
                fitView
                nodeTypes={nodeTypes}
                style={{
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    boxShadow:
                        '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
                }}
            >
                <Panel position="top-right">
                    <Radio.Group onChange={(e) => onLayout(e.target.value as 'TB' | 'LR')}>
                        <Radio.Button value="TB">вертикально</Radio.Button>
                        <Radio.Button value="LR">горизонтально</Radio.Button>
                    </Radio.Group>
                </Panel>
            </ReactFlow>
        </>
    )
})

export const EntitreeFlexTree = () => {
    // Core's CrudResource context is untyped; narrowed to the surface the tree uses.
    const crudContext = useCrudResourceContext() as CrudContextLike

    return <EntitreeFlexTreeObserver crudContext={crudContext} />
}
